import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import SearchableSelect from './SearchableSelect';
import { readWorkbookSheetNames, readSheetHeaderRow, bestFieldMatch, confidenceLabel, normalize } from '../utils/fileFieldMatch';
import { buildMatchFieldOptions, MATCH_TYPES } from '../utils/targetingListFields';
import { buildStatusGroupMap, buildProgramOptionGroups } from '../utils/statusGroups';
import SetColumnEditor from './SetColumnEditor';

const API = process.env.REACT_APP_API_URL;

// "Set Check" (2026-09-21) — a target of this list type carries several
// paired columns (column_config) instead of one match_field/match_type, and
// needs its own per-column target_column mapping step below (see
// columnTargetColumns) since one spreadsheet column now feeds one NAMED
// column of the target, not the whole target.
const SET_CHECK_LIST_TYPE = 'Set Check';

// A client's multi-tab workbook sometimes has a sheet that isn't a real list
// at all (a "DO NOT USE" placeholder, a pipeline/run-log status sheet) — these
// two checks catch the real cases seen in practice: a telling sheet NAME, or
// a first-header-cell literally saying "DO NOT USE" (seen on sheets whose
// name gave no hint at all). This only sets the step-2 checkbox's DEFAULT —
// every sheet stays fully PM-editable, nothing is hidden or blocked.
const JUNK_SHEET_NAME_RE = /do not use|pipeline|run log/i;
const isJunkHeaderCell = (h) => /^do not use$/i.test((h || '').trim());

// The overlay (not the panel) owns scrolling, and centers via flex-start +
// panel margin rather than alignItems:'center' — a shorter step's SearchableSelect
// dropdown (e.g. step 1's Program picker) needs to render below its field
// without getting clipped, which an overflow:auto PANEL would do the moment
// its own content is shorter than the dropdown's popup extent (real bug,
// caught live: the Program dropdown was cut off mid-list on step 1). Letting
// the panel grow naturally and scrolling the full-viewport overlay instead
// means nothing inside the panel is ever clipped by a too-small ancestor box.
const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px',
  overflowY: 'auto',
};
const panelStyle = {
  background: '#fff', border: '1px solid #ccc', borderRadius: '8px', width: 'min(980px, 96vw)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.25)', margin: '20px 0',
  fontSize: '13px', padding: '20px 24px', display: 'flex', flexDirection: 'column',
};
const btn = (color, disabled) => ({
  padding: '7px 16px', background: disabled ? '#bdc3c7' : color, color: '#fff', border: 'none',
  borderRadius: '4px', cursor: disabled ? 'default' : 'pointer', fontSize: '13px', fontWeight: '600',
});
const labelStyle = { fontSize: '12px', color: '#555', marginBottom: '4px', display: 'block', fontWeight: '600' };
const inputStyle = {
  padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px',
  fontSize: '13px', width: '100%', boxSizing: 'border-box',
};
const STEP_LABELS = ['Program', 'Files & Sheets', 'Targets & Mapping', 'Review', 'Import'];

export default function ClientListImportWizard({ onClose, onImported }) {
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');

  // ── Reference data ────────────────────────────────────────────────────
  const [clients, setClients] = useState([]);
  const [allPrograms, setAllPrograms] = useState([]);
  const [listTypes, setListTypes] = useState([]);
  const [allLeadFields, setAllLeadFields] = useState([]);
  // Closed programs nest into a collapsible section on the Program picker
  // below instead of mixing into the flat list — see src/utils/statusGroups.js.
  const [programStatuses, setProgramStatuses] = useState([]);

  useEffect(() => {
    axios.get(`${API}/clients/`).then(r => setClients(r.data)).catch(() => {});
    axios.get(`${API}/programs/`).then(r => setAllPrograms(r.data)).catch(() => {});
    axios.get(`${API}/lookup-values/?category=targeting_list_type`).then(r => setListTypes(r.data)).catch(() => {});
    axios.get(`${API}/leads/export-fields`).then(r => setAllLeadFields(r.data || [])).catch(() => {});
    axios.get(`${API}/lookup-values/?category=program_status`).then(r => setProgramStatuses(r.data)).catch(() => {});
  }, []);

  // ── Step 1: Program ───────────────────────────────────────────────────
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const programOptions = selectedClientId
    ? allPrograms.filter(p => String(p.client_id) === String(selectedClientId))
    : allPrograms;
  const programOptionGroups = buildProgramOptionGroups(programOptions, buildStatusGroupMap(programStatuses));
  const selectedProgram = allPrograms.find(p => String(p.id) === String(selectedProgramId));

  // ── Step 2: Files & sheets ────────────────────────────────────────────
  // uploadedFiles[i] = {
  //   id, file, filename, loading, error,
  //   sheetNames: string[],
  //   headers: { [sheetName]: string[] },
  //   includedSheets: { [sheetName]: bool },
  //   sheetTags: { [sheetName]: 'excluded-by-default' | null },
  // }
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileSeqRef = useRef(0);
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFilesSelected = async (fileList) => {
    const arr = Array.from(fileList || []);
    for (const file of arr) {
      const id = fileSeqRef.current++;
      const entry = {
        id, file, filename: file.name, loading: true, error: null,
        sheetNames: [], headers: {}, includedSheets: {}, sheetTags: {},
      };
      setUploadedFiles(prev => [...prev, entry]);
      try {
        const sheetNames = await readWorkbookSheetNames(file);
        if (!sheetNames.length) {
          setUploadedFiles(prev => prev.map(f => f.id === id ? {
            ...f, loading: false,
            error: 'Not a valid XLSX workbook (or it has no sheet tabs) — this wizard needs an Excel file with one sheet per list.',
          } : f));
          continue;
        }
        const headers = {};
        const includedSheets = {};
        const sheetTags = {};
        for (const sheetName of sheetNames) {
          let hdrs = [];
          try { hdrs = await readSheetHeaderRow(file, sheetName); } catch { hdrs = []; }
          headers[sheetName] = hdrs;
          const junk = JUNK_SHEET_NAME_RE.test(sheetName) || hdrs.some(isJunkHeaderCell) || hdrs.length === 0;
          sheetTags[sheetName] = junk ? 'excluded-by-default' : null;
          includedSheets[sheetName] = !junk;
        }
        setUploadedFiles(prev => prev.map(f => f.id === id
          ? { ...f, sheetNames, headers, includedSheets, sheetTags, loading: false }
          : f));
      } catch (e) {
        setUploadedFiles(prev => prev.map(f => f.id === id
          ? { ...f, loading: false, error: e.message || 'Could not read that file.' }
          : f));
      }
    }
  };

  const toggleSheet = (fileId, sheetName) => {
    setUploadedFiles(prev => prev.map(f => f.id !== fileId ? f : {
      ...f, includedSheets: { ...f.includedSheets, [sheetName]: !f.includedSheets[sheetName] },
    }));
  };
  const removeFile = (fileId) => setUploadedFiles(prev => prev.filter(f => f.id !== fileId));

  const anySheetIncluded = uploadedFiles.some(f => f.sheetNames.some(s => f.includedSheets[s]));

  // ── Step 3: Targets & column mapping ──────────────────────────────────
  // targets[key] = { label, mode: 'new'|'existing',
  //                  list_name?, list_type, match_field, match_type?, list_id? }
  const [targets, setTargets] = useState({});
  const targetSeqRef = useRef(1);
  // normalize(header) -> targetKey — remembers a manual pick across every
  // later sheet/file in this session that shares that header text.
  const [headerMemory, setHeaderMemory] = useState({});
  // columnMappings[`${fileId}:${sheetName}`] = { [columnIndex]: targetKey }
  const [columnMappings, setColumnMappings] = useState({});

  const [showAddTarget, setShowAddTarget] = useState(false);
  const [addTargetMode, setAddTargetMode] = useState('new');
  const [newTargetForm, setNewTargetForm] = useState({ label: '', list_type: '', match_field: '', match_type: 'exact', column_config: [{ name: '', lead_field: '', match_type: 'exact' }] });
  const [existingSearchType, setExistingSearchType] = useState('');
  const [existingSearchResults, setExistingSearchResults] = useState([]);
  const [existingSearchSelected, setExistingSearchSelected] = useState('');

  useEffect(() => {
    if (!showAddTarget || addTargetMode !== 'existing') return;
    const params = new URLSearchParams();
    if (existingSearchType) params.set('list_type', existingSearchType);
    if (selectedProgram?.client_id) params.set('client_id', selectedProgram.client_id);
    axios.get(`${API}/targeting-lists/?${params.toString()}`)
      .then(r => setExistingSearchResults(r.data || []))
      .catch(() => setExistingSearchResults([]));
  }, [showAddTarget, addTargetMode, existingSearchType, selectedProgram]);

  const openAddTarget = () => {
    setNewTargetForm({ label: '', list_type: listTypes[0]?.value || '', match_field: '', match_type: 'exact', column_config: [{ name: '', lead_field: '', match_type: 'exact' }] });
    setAddTargetMode('new');
    setExistingSearchType('');
    setExistingSearchSelected('');
    setShowAddTarget(true);
  };

  // Shared by the top "+ Add Target" panel and the per-column inline
  // "+ Create new list for this column" creator below — one target = one
  // new TargetingList, named "{program} - {label}". Returns the new
  // target's key, or null (and sets an error) if the form's incomplete.
  const addNewTarget = (form) => {
    const isSetCheck = form.list_type === SET_CHECK_LIST_TYPE;
    const validCols = isSetCheck ? (form.column_config || []).filter(c => c.name.trim() && c.lead_field) : null;
    if (!form.label.trim() || !form.list_type || (isSetCheck ? !validCols.length : !form.match_field)) {
      setError(isSetCheck
        ? 'Please fill in a label, list type, and at least one column with a name and matching Lead field.'
        : 'Please fill in a label, list type, and match field.');
      return null;
    }
    const key = `t${targetSeqRef.current++}`;
    const listName = selectedProgram ? `${selectedProgram.program_name} - ${form.label}` : form.label;
    setTargets(prev => ({ ...prev, [key]: isSetCheck ? {
      label: form.label, mode: 'new', list_name: listName, list_type: form.list_type,
      match_field: validCols[0].name, column_config: validCols,
    } : {
      label: form.label, mode: 'new', list_name: listName,
      list_type: form.list_type, match_field: form.match_field, match_type: form.match_type,
    } }));
    setError('');
    return key;
  };

  const confirmAddTarget = () => {
    if (addTargetMode === 'new') {
      if (!addNewTarget(newTargetForm)) return;
    } else {
      const found = existingSearchResults.find(l => String(l.id) === String(existingSearchSelected));
      if (!found) { setError('Please select an existing list.'); return; }
      const key = `t${targetSeqRef.current++}`;
      setTargets(prev => ({ ...prev, [key]: {
        label: found.list_name, mode: 'existing', list_id: found.id,
        list_type: found.list_type, match_field: found.match_field,
      } }));
      setError('');
    }
    setShowAddTarget(false);
  };

  // ── Inline "+ Create new list for this column" (step 3 mapping grid) ──
  // The fast path: rather than making the PM pre-define every target in the
  // panel above before any mapping is possible, each column's own dropdown
  // can spin up a brand-new target on the spot, scoped to just that column.
  // Root cause of a real live report this was added for: a PM defined only
  // ONE target (the panel above was the only way in, so it was easy to stop
  // there) and both a "domain" and a "parent_company_name" column ended up
  // mapped to it, silently combining two different concepts into one list.
  const [creatingTargetFor, setCreatingTargetFor] = useState(null);   // { fileId, sheetName, colIdx, header } | null
  const [inlineTargetForm, setInlineTargetForm] = useState({ label: '', list_type: '', match_field: '', match_type: 'exact', column_config: [{ name: '', lead_field: '', match_type: 'exact' }] });

  const startInlineCreate = (fileId, sheetName, colIdx, header) => {
    const guess = bestFieldMatch(header, buildMatchFieldOptions(allLeadFields));
    setInlineTargetForm({
      label: header,
      list_type: listTypes.find(t => /account list/i.test(t.value))?.value || listTypes[0]?.value || '',
      match_field: guess ? guess.field.value : '',
      match_type: 'exact',
      column_config: [{ name: header, lead_field: guess ? guess.field.value : '', match_type: 'exact' }],
    });
    setCreatingTargetFor({ fileId, sheetName, colIdx, header });
    setError('');
  };

  const confirmInlineCreate = () => {
    const key = addNewTarget(inlineTargetForm);
    if (!key) return;
    const { fileId, sheetName, colIdx, header } = creatingTargetFor;
    setColumnTarget(fileId, sheetName, colIdx, header, key);
    if (inlineTargetForm.list_type === SET_CHECK_LIST_TYPE) {
      const firstCol = (inlineTargetForm.column_config || []).find(c => c.name.trim() && c.lead_field);
      if (firstCol) setColumnTargetColumn(fileId, sheetName, colIdx, firstCol.name.trim());
    }
    setCreatingTargetFor(null);
  };
  const cancelInlineCreate = () => { setCreatingTargetFor(null); setError(''); };

  const removeTarget = (key) => {
    setTargets(prev => { const n = { ...prev }; delete n[key]; return n; });
    setColumnMappings(prev => {
      const next = {};
      Object.entries(prev).forEach(([sk, cols]) => {
        const filtered = {};
        Object.entries(cols).forEach(([ci, tk]) => { if (tk !== key) filtered[ci] = tk; });
        next[sk] = filtered;
      });
      return next;
    });
    setHeaderMemory(prev => {
      const next = {};
      Object.entries(prev).forEach(([h, tk]) => { if (tk !== key) next[h] = tk; });
      return next;
    });
  };

  // Seed each included, not-yet-touched sheet's column mapping once it's
  // reachable — header memory first, then a fuzzy guess against the
  // session's defined targets, else unmapped. Never re-seeds a sheet the PM
  // has already edited (a mapping object already exists for it).
  useEffect(() => {
    if (step !== 3) return;
    setColumnMappings(prev => {
      let changed = false;
      const next = { ...prev };
      uploadedFiles.forEach(f => {
        f.sheetNames.forEach(sheetName => {
          if (!f.includedSheets[sheetName]) return;
          const mapKey = `${f.id}:${sheetName}`;
          if (next[mapKey]) return;
          changed = true;
          const hdrs = f.headers[sheetName] || [];
          const candidates = Object.entries(targets).map(([k, t]) => ({ key: k, label: t.label }));
          const seeded = {};
          hdrs.forEach((h, idx) => {
            const norm = normalize(h);
            if (headerMemory[norm]) { seeded[idx] = headerMemory[norm]; return; }
            const guess = candidates.length ? bestFieldMatch(h, candidates) : null;
            if (guess) seeded[idx] = guess.field.key;
          });
          next[mapKey] = seeded;
        });
      });
      return changed ? next : prev;
    });
  }, [step, uploadedFiles, targets, headerMemory]);

  const setColumnTarget = (fileId, sheetName, colIdx, header, targetKey) => {
    const mapKey = `${fileId}:${sheetName}`;
    setColumnMappings(prev => ({ ...prev, [mapKey]: { ...(prev[mapKey] || {}), [colIdx]: targetKey || undefined } }));
    if (targetKey) setHeaderMemory(prev => ({ ...prev, [normalize(header)]: targetKey }));
  };

  // Only meaningful when the mapped target is a Set Check target — names
  // which of that target's column_config columns this spreadsheet column
  // feeds. columnMappings itself stays a plain targetKey string for every
  // other (single-column) target, so this is a separate, additive map rather
  // than changing that shape.
  const [columnTargetColumns, setColumnTargetColumns] = useState({});
  const setColumnTargetColumn = (fileId, sheetName, colIdx, targetColumn) => {
    const mapKey = `${fileId}:${sheetName}`;
    setColumnTargetColumns(prev => ({ ...prev, [mapKey]: { ...(prev[mapKey] || {}), [colIdx]: targetColumn || undefined } }));
  };

  const includedSheetList = () => {
    const out = [];
    uploadedFiles.forEach(f => {
      f.sheetNames.forEach(sheetName => {
        if (f.includedSheets[sheetName]) out.push({ file: f, sheetName });
      });
    });
    return out;
  };

  // ── Step 4/5: build plan + submit + poll ──────────────────────────────
  const buildSubmission = () => {
    const filesToSend = [];
    const filePlans = [];
    uploadedFiles.forEach(f => {
      const sheets = [];
      f.sheetNames.forEach(sheetName => {
        if (!f.includedSheets[sheetName]) return;
        const mapKey = `${f.id}:${sheetName}`;
        const mapping = columnMappings[mapKey] || {};
        const targetCols = columnTargetColumns[mapKey] || {};
        const hdrs = f.headers[sheetName] || [];
        const columns = Object.entries(mapping)
          .filter(([, tk]) => !!tk)
          .map(([idx, tk]) => ({
            header: hdrs[Number(idx)] || '', column_index: Number(idx), target_key: tk,
            target_column: targetCols[idx] || undefined,
          }));
        if (columns.length) sheets.push({ sheet_name: sheetName, columns });
      });
      if (sheets.length) {
        const file_index = filesToSend.length;
        filesToSend.push(f.file);
        filePlans.push({ file_index, filename: f.filename, sheets });
      }
    });
    const targetsPlan = {};
    Object.entries(targets).forEach(([key, t]) => {
      targetsPlan[key] = t.mode === 'existing'
        ? { mode: 'existing', list_id: t.list_id }
        : {
            mode: 'new', list_name: t.list_name, list_type: t.list_type,
            match_field: t.match_field, match_type: t.match_type,
            column_config: t.column_config,
          };
    });
    return { plan: { program_id: Number(selectedProgramId), files: filePlans, targets: targetsPlan }, filesToSend };
  };

  const [submitting, setSubmitting] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const pollRef = useRef(null);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const startPolling = (id) => {
    const poll = () => {
      axios.get(`${API}/targeting-lists/import-jobs/${id}`).then(r => {
        setJobStatus(r.data);
        if (r.data.status === 'completed' || r.data.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }).catch(() => {});
    };
    poll();
    pollRef.current = setInterval(poll, 1500);
  };

  const handleSubmit = () => {
    const { plan, filesToSend } = buildSubmission();
    if (!filesToSend.length) { setError('No sheets are mapped to a target yet — nothing to import.'); return; }
    setSubmitting(true);
    setError('');
    const fd = new FormData();
    fd.append('plan', JSON.stringify(plan));
    filesToSend.forEach(f => fd.append('files', f));
    axios.post(`${API}/targeting-lists/import-jobs`, fd)
      .then(r => { setStep(5); startPolling(r.data.job_id); })
      .catch(err => setError(err.response?.data?.detail || String(err)))
      .finally(() => setSubmitting(false));
  };

  // ── Step gating ────────────────────────────────────────────────────────
  const canGoNext = () => {
    if (step === 1) return !!selectedProgramId;
    if (step === 2) return anySheetIncluded;
    if (step === 3) {
      const { filesToSend } = buildSubmission();
      return filesToSend.length > 0;
    }
    return true;
  };

  const targetOptions = Object.entries(targets).map(([k, t]) => ({ key: k, label: t.label }));
  const { plan: reviewPlan } = step >= 4 ? buildSubmission() : { plan: null };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={panelStyle} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <h3 style={{ margin: 0 }}>Client List Processor</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#888' }}>✕</button>
        </div>
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '16px' }}>
          Step {step} of 5 — {STEP_LABELS[step - 1]}
        </div>

        {error && (
          <div style={{ background: '#fdedec', border: '1px solid #f5b7b1', color: '#c0392b',
                        borderRadius: '6px', padding: '10px 14px', marginBottom: '14px', fontSize: '12px' }}>
            {error}
          </div>
        )}

        {/* ── Step 1: Program ───────────────────────────────────────────── */}
        {step === 1 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '14px' }}>
            <div>
              <label style={labelStyle}>Client (optional, narrows the program list)</label>
              <select value={selectedClientId} onChange={e => { setSelectedClientId(e.target.value); setSelectedProgramId(''); }} style={inputStyle}>
                <option value="">All clients</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Program *</label>
              <SearchableSelect
                options={programOptionGroups.options}
                closedOptions={programOptionGroups.closedOptions}
                value={selectedProgramId}
                onChange={setSelectedProgramId}
                placeholder="Search programs…"
              />
            </div>
          </div>
        )}

        {/* ── Step 2: Files & sheets ────────────────────────────────────── */}
        {step === 2 && (
          <div>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); handleFilesSelected(e.dataTransfer.files); }}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver ? '#8e44ad' : '#ccc'}`, borderRadius: '8px', padding: '18px',
                textAlign: 'center', cursor: 'pointer', marginBottom: '16px',
                background: dragOver ? '#f6eefc' : '#fafafa', color: '#666', fontSize: '13px',
              }}
            >
              📁 Drop one or more client XLSX workbooks here, or click to browse — each sheet becomes a pickable list source.
              <input ref={fileInputRef} type="file" accept=".xlsx" multiple style={{ display: 'none' }}
                     onChange={e => { handleFilesSelected(e.target.files); e.target.value = ''; }} />
            </div>

            {uploadedFiles.map(f => (
              <div key={f.id} style={{ border: '1px solid #e4e9ef', borderRadius: '6px', padding: '10px 14px', marginBottom: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong style={{ fontSize: '13px' }}>{f.filename}</strong>
                  <button onClick={() => removeFile(f.id)} style={{ background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', fontSize: '12px' }}>Remove</button>
                </div>
                {f.loading && <div style={{ fontSize: '12px', color: '#888', marginTop: '6px' }}>Reading sheets…</div>}
                {f.error && <div style={{ fontSize: '12px', color: '#c0392b', marginTop: '6px' }}>{f.error}</div>}
                {!f.loading && !f.error && (
                  <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {f.sheetNames.map(sheetName => (
                      <label key={sheetName} style={{
                        display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px',
                        padding: '4px 8px', borderRadius: '4px',
                        background: f.sheetTags[sheetName] ? '#fdf6ec' : '#f4f6f7',
                        border: `1px solid ${f.sheetTags[sheetName] ? '#f5cba7' : '#e0e6ed'}`,
                      }}>
                        <input type="checkbox" checked={!!f.includedSheets[sheetName]} onChange={() => toggleSheet(f.id, sheetName)} />
                        {sheetName}
                        {f.sheetTags[sheetName] && <span style={{ color: '#b9770e' }}>looks unusable</span>}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {!uploadedFiles.length && <div style={{ fontSize: '12px', color: '#999' }}>No files added yet.</div>}
          </div>
        )}

        {/* ── Step 3: Targets & column mapping ──────────────────────────── */}
        {step === 3 && (
          <div>
            <div style={{ border: '1px solid #e4e9ef', borderRadius: '6px', padding: '12px 14px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <strong style={{ fontSize: '13px' }}>Targets defined this session</strong>
                <button onClick={openAddTarget} style={btn('#2c3e50')}>+ Add Target</button>
              </div>
              {!Object.keys(targets).length && (
                <div style={{ fontSize: '12px', color: '#999' }}>
                  None yet — use this to search for or set up a list you'll reuse across several sheets/files. For a
                  one-off, you can also just pick "+ Create new list for this column…" directly in a column's dropdown below.
                </div>
              )}
              {Object.entries(targets).map(([key, t]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', padding: '4px 0' }}>
                  <span style={{ fontWeight: '600' }}>{t.label}</span>
                  <span style={{ color: '#888' }}>{t.mode === 'existing' ? `existing list #${t.list_id}` : `new — ${t.list_name}`}</span>
                  <span style={{ color: '#888' }}>type: {t.list_type} · match: {t.match_field}</span>
                  <button onClick={() => removeTarget(key)} style={{ background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer' }}>✕</button>
                </div>
              ))}

              {showAddTarget && (
                <div style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                  <div style={{ display: 'flex', gap: '14px', marginBottom: '10px', fontSize: '12px' }}>
                    <label><input type="radio" checked={addTargetMode === 'new'} onChange={() => setAddTargetMode('new')} /> Create new list</label>
                    <label><input type="radio" checked={addTargetMode === 'existing'} onChange={() => setAddTargetMode('existing')} /> Use existing list</label>
                  </div>
                  {addTargetMode === 'new' ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                      <div>
                        <label style={labelStyle}>Label *</label>
                        <input value={newTargetForm.label} onChange={e => setNewTargetForm({ ...newTargetForm, label: e.target.value })}
                               placeholder="e.g. TAL Domain" style={inputStyle} />
                        {selectedProgram && newTargetForm.label && (
                          <div style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>
                            Will be named: {selectedProgram.program_name} - {newTargetForm.label}
                          </div>
                        )}
                      </div>
                      <div>
                        <label style={labelStyle}>List Type *</label>
                        <select value={newTargetForm.list_type} onChange={e => setNewTargetForm({ ...newTargetForm, list_type: e.target.value })} style={inputStyle}>
                          <option value="">Select type…</option>
                          {listTypes.map(t => <option key={t.id} value={t.value}>{t.value}</option>)}
                        </select>
                      </div>
                      {newTargetForm.list_type === SET_CHECK_LIST_TYPE ? (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <label style={labelStyle}>Columns *</label>
                          <SetColumnEditor
                            columns={newTargetForm.column_config}
                            onChange={cols => setNewTargetForm({ ...newTargetForm, column_config: cols })}
                            allLeadFields={allLeadFields}
                          />
                        </div>
                      ) : (
                        <>
                          <div>
                            <label style={labelStyle}>Match Field *</label>
                            <SearchableSelect
                              options={buildMatchFieldOptions(allLeadFields, newTargetForm.match_field)}
                              value={newTargetForm.match_field}
                              onChange={v => setNewTargetForm({ ...newTargetForm, match_field: v })}
                              placeholder="Search fields…"
                            />
                          </div>
                          <div>
                            <label style={labelStyle}>Match Type</label>
                            <select value={newTargetForm.match_type} onChange={e => setNewTargetForm({ ...newTargetForm, match_type: e.target.value })} style={inputStyle}>
                              {Object.entries(MATCH_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                      <div>
                        <label style={labelStyle}>List Type (optional filter)</label>
                        <select value={existingSearchType} onChange={e => setExistingSearchType(e.target.value)} style={inputStyle}>
                          <option value="">Any type</option>
                          {listTypes.map(t => <option key={t.id} value={t.value}>{t.value}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>List *</label>
                        <SearchableSelect
                          options={existingSearchResults.map(l => ({ value: l.id, label: `${l.list_name} (${l.list_type} · ${l.match_field || '—'})` }))}
                          value={existingSearchSelected}
                          onChange={setExistingSearchSelected}
                          placeholder="Search existing lists…"
                        />
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    <button onClick={confirmAddTarget} style={btn('#27ae60')}>Add Target</button>
                    <button onClick={() => setShowAddTarget(false)} style={btn('#95a5a6')}>Cancel</button>
                  </div>
                </div>
              )}
            </div>

            {includedSheetList().map(({ file, sheetName }) => {
              const mapKey = `${file.id}:${sheetName}`;
              const mapping = columnMappings[mapKey] || {};
              const targetCols = columnTargetColumns[mapKey] || {};
              const hdrs = file.headers[sheetName] || [];
              const usedKeys = Object.values(mapping).filter(Boolean);
              // A Set Check target legitimately receives several columns at
              // once (one per configured field) — only warn on a genuine
              // duplicate: the same target AND the same target_column.
              const usedPairs = Object.entries(mapping)
                .filter(([, tk]) => !!tk)
                .map(([idx, tk]) => `${tk}::${targets[tk]?.column_config ? (targetCols[idx] || '') : ''}`);
              const hasDuplicateTarget = new Set(usedPairs).size < usedPairs.length;
              const mappedCount = usedKeys.length;
              return (
                <div key={mapKey} style={{ border: '1px solid #e4e9ef', borderRadius: '6px', padding: '10px 14px', marginBottom: '10px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>
                    {file.filename} — {sheetName} <span style={{ color: '#888', fontWeight: '400' }}>({mappedCount} column{mappedCount === 1 ? '' : 's'} mapped)</span>
                  </div>
                  {hasDuplicateTarget && (
                    <div style={{ fontSize: '11px', color: '#b9770e', background: '#fdf6ec', border: '1px solid #f5cba7',
                                  borderRadius: '4px', padding: '5px 8px', marginBottom: '6px' }}>
                      ⚠ Two or more columns here are mapped to the <strong>same</strong> list — their values will be combined
                      into one list. Give each column its own target below if that's not what you want.
                    </div>
                  )}
                  <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                    {hdrs.map((h, idx) => {
                      const currentTarget = mapping[idx] || '';
                      const guess = targetOptions.length ? bestFieldMatch(h, targetOptions) : null;
                      const isCreatingHere = creatingTargetFor && creatingTargetFor.fileId === file.id
                        && creatingTargetFor.sheetName === sheetName && creatingTargetFor.colIdx === idx;
                      return (
                        <React.Fragment key={idx}>
                          <div style={{
                            display: 'grid', gridTemplateColumns: targets[currentTarget]?.column_config ? '1fr 1fr 1fr 100px' : '1fr 1fr 100px', gap: '8px',
                            alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #f5f5f5', fontSize: '12px',
                          }}>
                            <span title={h}>{h}</span>
                            <select value={currentTarget} onChange={e => {
                              const v = e.target.value;
                              if (v === '__new__') { startInlineCreate(file.id, sheetName, idx, h); return; }
                              setColumnTarget(file.id, sheetName, idx, h, v);
                            }} style={inputStyle}>
                              <option value="">— skip this column —</option>
                              <option value="__new__">+ Create new list for this column…</option>
                              {targetOptions.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                            </select>
                            {targets[currentTarget]?.column_config && (
                              <select
                                value={targetCols[idx] || ''}
                                onChange={e => setColumnTargetColumn(file.id, sheetName, idx, e.target.value)}
                                style={inputStyle}
                              >
                                <option value="">— which column? —</option>
                                {targets[currentTarget].column_config.map(c => (
                                  <option key={c.name} value={c.name}>{c.name}</option>
                                ))}
                              </select>
                            )}
                            <span style={{ color: !currentTarget ? '#aaa' : guess && guess.field.key === currentTarget ? '#1a7f4b' : '#888' }}>
                              {!currentTarget ? '—' : guess && guess.field.key === currentTarget ? confidenceLabel(guess.score) : 'Manual'}
                            </span>
                          </div>
                          {isCreatingHere && (
                            <div style={{ background: '#f6eefc', border: '1px solid #d9b8f0', borderRadius: '6px', padding: '10px', margin: '4px 0' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '8px' }}>
                                <div>
                                  <label style={labelStyle}>Label *</label>
                                  <input value={inlineTargetForm.label}
                                         onChange={e => setInlineTargetForm({ ...inlineTargetForm, label: e.target.value })}
                                         style={inputStyle} />
                                </div>
                                <div>
                                  <label style={labelStyle}>List Type *</label>
                                  <select value={inlineTargetForm.list_type}
                                          onChange={e => setInlineTargetForm({ ...inlineTargetForm, list_type: e.target.value })} style={inputStyle}>
                                    <option value="">Select type…</option>
                                    {listTypes.map(t => <option key={t.id} value={t.value}>{t.value}</option>)}
                                  </select>
                                </div>
                                {inlineTargetForm.list_type !== SET_CHECK_LIST_TYPE && (
                                  <div>
                                    <label style={labelStyle}>Match Field *</label>
                                    <SearchableSelect
                                      options={buildMatchFieldOptions(allLeadFields, inlineTargetForm.match_field)}
                                      value={inlineTargetForm.match_field}
                                      onChange={v => setInlineTargetForm({ ...inlineTargetForm, match_field: v })}
                                      placeholder="Search fields…"
                                    />
                                  </div>
                                )}
                              </div>
                              {inlineTargetForm.list_type === SET_CHECK_LIST_TYPE && (
                                <div style={{ margin: '8px 0' }}>
                                  <label style={labelStyle}>Columns *</label>
                                  <SetColumnEditor
                                    columns={inlineTargetForm.column_config}
                                    onChange={cols => setInlineTargetForm({ ...inlineTargetForm, column_config: cols })}
                                    allLeadFields={allLeadFields}
                                  />
                                  <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                    This column will feed the FIRST configured column above — map any other columns for this same target afterward via the "which column?" picker.
                                  </div>
                                </div>
                              )}
                              {selectedProgram && inlineTargetForm.label && (
                                <div style={{ fontSize: '11px', color: '#888', margin: '6px 0' }}>
                                  Will be named: {selectedProgram.program_name} - {inlineTargetForm.label}
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                                <button onClick={confirmInlineCreate} style={btn('#27ae60')}>Create &amp; Map</button>
                                <button onClick={cancelInlineCreate} style={btn('#95a5a6')}>Cancel</button>
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Step 4: Review ─────────────────────────────────────────────── */}
        {step === 4 && reviewPlan && (
          <div>
            <div style={{ fontSize: '13px', marginBottom: '10px' }}>
              Program: <strong>{selectedProgram?.program_name}</strong>
            </div>
            <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Targets</div>
            {Object.entries(targets).map(([key, t]) => (
              <div key={key} style={{ fontSize: '12px', padding: '3px 0' }}>
                <strong>{t.label}</strong> — {t.mode === 'existing' ? `existing list #${t.list_id}` : `will create "${t.list_name}"`} ({t.list_type}, match: {t.match_field})
              </div>
            ))}
            <div style={{ fontSize: '12px', fontWeight: '600', margin: '14px 0 6px' }}>Files &amp; sheets to import</div>
            {reviewPlan.files.map(fp => (
              <div key={fp.file_index} style={{ fontSize: '12px', padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                <strong>{fp.filename}</strong>
                {fp.sheets.map(sp => (
                  <div key={sp.sheet_name} style={{ marginLeft: '14px', color: '#555' }}>
                    {sp.sheet_name}: {sp.columns.map(c => `"${c.header}" → ${targets[c.target_key]?.label || c.target_key}`).join(', ')}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* ── Step 5: Submit & progress ──────────────────────────────────── */}
        {step === 5 && (
          <div>
            {!jobStatus || (jobStatus.status !== 'completed' && jobStatus.status !== 'failed') ? (
              <div>
                <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                  {jobStatus?.current_label ? `Processing: ${jobStatus.current_label}` : 'Starting…'}
                </div>
                <div style={{ height: '8px', background: '#eee', borderRadius: '4px', overflow: 'hidden', maxWidth: '400px' }}>
                  <div style={{
                    height: '100%', background: '#8e44ad', borderRadius: '4px',
                    width: `${jobStatus && jobStatus.total_sheets ? Math.round((jobStatus.processed_sheets / jobStatus.total_sheets) * 100) : 0}%`,
                    transition: 'width 0.4s ease',
                  }} />
                </div>
                <div style={{ fontSize: '12px', color: '#888', marginTop: '6px' }}>
                  {jobStatus ? `${jobStatus.processed_sheets} / ${jobStatus.total_sheets} sheets · ${jobStatus.items_created} items added` : ''}
                </div>
              </div>
            ) : jobStatus.status === 'completed' ? (
              <div>
                <div style={{ fontSize: '13px', color: '#1e8449', fontWeight: '600', marginBottom: '10px' }}>✓ Import complete</div>
                {(jobStatus.result_lists || []).map(l => (
                  <div key={l.list_id} style={{ fontSize: '12px', padding: '3px 0' }}>
                    <strong>{l.list_name}</strong> ({l.list_type}, match: {l.match_field}) — {l.items_added} item{l.items_added === 1 ? '' : 's'} added
                  </div>
                ))}
                {(jobStatus.warnings || []).length > 0 && (
                  <div style={{ marginTop: '10px', fontSize: '12px', color: '#b9770e' }}>
                    {jobStatus.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
                  </div>
                )}
                <button onClick={() => { onImported && onImported(jobStatus.result_lists); }} style={{ ...btn('#27ae60'), marginTop: '14px' }}>
                  Done
                </button>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '13px', color: '#c0392b', fontWeight: '600', marginBottom: '8px' }}>Import failed</div>
                <div style={{ fontSize: '12px', color: '#c0392b' }}>{jobStatus.error}</div>
                <button onClick={onClose} style={{ ...btn('#95a5a6'), marginTop: '14px' }}>Close</button>
              </div>
            )}
          </div>
        )}

        {/* ── Nav ────────────────────────────────────────────────────────── */}
        {step < 5 && (
          <div style={{ display: 'flex', gap: '8px', marginTop: '18px', borderTop: '1px solid #eee', paddingTop: '14px' }}>
            {step > 1 && <button onClick={() => { setError(''); setStep(s => s - 1); }} style={btn('#95a5a6')}>Back</button>}
            {step < 4 && (
              <button onClick={() => { setError(''); setStep(s => s + 1); }} disabled={!canGoNext()} style={btn('#8e44ad', !canGoNext())}>
                Next
              </button>
            )}
            {step === 4 && (
              <button onClick={handleSubmit} disabled={submitting} style={btn('#27ae60', submitting)}>
                {submitting ? 'Starting…' : '🚀 Import'}
              </button>
            )}
            <button onClick={onClose} style={{ ...btn('#eee'), color: '#555' }}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}
